import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import * as XLSX from 'xlsx';
import { FileDataPropertyType, IFileDataPropertySchema, IFileDataSchema, IFileDataSchemaProperties, IFileMapPropertySchema, IFileTransfer, IImportedData } from './interfaces';

export interface xlsxImportError {
	error: string;
	row: number;
	value: string;
}

/**
 * Hard limit the `.xlsx` format places on a single cell's character count.
 *
 * Enforced here rather than per entity because the cap is a property of the file format, not of any one schema — so the layer that knows the format is the layer that enforces it, and every entity gets a clear error instead of a silently truncated or malformed workbook.
 */
export const EXCEL_MAX_CELL_LENGTH = 32767;

/** Characters of an over-cap cell echoed back in the error, so the payload stays bounded. */
const OVER_CAP_PREVIEW_LENGTH = 50;

@Injectable({ providedIn: 'root' })
export class ExcelFileTransfer<T> implements IFileTransfer<T, xlsxImportError> {
	constructor(private translator: TranslateService) {}

	private buildHeader<TRow>(schema: IFileDataSchemaProperties<TRow>): string[] {
		return Object.entries(schema).map(([key, property]) => {
			const prop = property as IFileDataPropertySchema<any>;
			return prop.fileKey || key; // Use fileKey if available, otherwise use key
		});
	}

	/** Throws if a cell about to be written exceeds the format's limit. Never truncates — a truncated `sqlQuery` or `detailView` is a corrupt export that looks like a successful one. */
	private assertCellLength(entity: string, schemaKey: string, value: unknown): void {
		if (typeof value === 'string' && value.length > EXCEL_MAX_CELL_LENGTH) {
			throw new RangeError(
				this.translator.instant('transferService.excel.errors.cell-too-long', {
					entity,
					schemaKey,
					length: value.length,
					maxLength: EXCEL_MAX_CELL_LENGTH,
				}),
			);
		}
	}

	/**
	 * Header row plus one row per entity, every cell length-checked, with `!cols` widths from the schema's `width`. Shared by {@link exportData} and {@link generateTemplate} so a template can never be built by different rules than a real export.
	 *
	 * Widths live here rather than in the template path alone because they are pure presentation: a real export is the file a user actually opens and reads, so a column too narrow to read is as unhelpful there as in a blank template. Header comments stay template-only — those are instructions for filling a file in,
	 * not for reading one.
	 */
	private buildSheet<TRow>(data: TRow[], schema: IFileDataSchema<TRow>): XLSX.WorkSheet {
		const properties = Object.entries<IFileDataPropertySchema<any>>(schema.properties);
		const rows: Array<Array<string | number | boolean>> = [this.buildHeader(schema.properties)];

		for (const rowData of data) {
			rows.push(
				properties.map(([key, propSchema]) => {
					const value = rowData[key];
					if (value === undefined || value === null || value === '') {
						return undefined; // Leave the cell blank for a missing value
					}

					const columnValue = this.buildColumnValue(key, value, propSchema.type);
					// Fail before anything is written, so a too-long cell never reaches the file
					this.assertCellLength(schema.name, propSchema.fileKey || key, columnValue);
					return columnValue;
				}),
			);
		}

		const ws = XLSX.utils.aoa_to_sheet(rows);

		const columnWidths = properties.map(([, propSchema]) => (propSchema.width ? { wch: propSchema.width } : {}));
		if (columnWidths.some((width) => 'wch' in width)) {
			ws['!cols'] = columnWidths;
		}

		return ws;
	}

	/** The sheet is ALWAYS named after the schema; `fileName` only names the output file. `importData` matches on `schema.name`, so sourcing both from one place makes export and import structurally incapable of disagreeing. */
	private writeSheet(fileName: string, schema: IFileDataSchema<T>, ws: XLSX.WorkSheet): void {
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, schema.name);
		XLSX.writeFile(wb, `${fileName}.xlsx`, { bookType: 'xlsx' });
	}

	public exportData(fileName: string, data: T[], schema: IFileDataSchema<T>): Observable<T[]> {
		this.writeSheet(fileName, schema, this.buildSheet(data, schema));

		return of(data);
	}

	/** Several differently-shaped sheets (e.g. a config's single settings row alongside its list-shaped sub-tables) written into ONE workbook/file, instead of one file per sheet. Each sheet still goes through {@link buildSheet}, so cell-length checks and column widths behave identically to a single-sheet export. */
	public exportWorkbook(fileName: string, sheets: Array<{ schema: IFileDataSchema<any>; data: any[] }>): Observable<void> {
		const wb = XLSX.utils.book_new();
		for (const sheet of sheets) {
			XLSX.utils.book_append_sheet(wb, this.buildSheet(sheet.data, sheet.schema), sheet.schema.name);
		}
		XLSX.writeFile(wb, `${fileName}.xlsx`, { bookType: 'xlsx' });

		return of(undefined);
	}

	/**
	 * Writes `${fileName}.xlsx` containing a single sheet named `schema.name`: the header row and `!cols` widths from {@link buildSheet} (so a template can never drift from the import contract), exactly one example row from the properties' `example` values, and each header cell carrying its `comment` resolved through
	 * TranslateService.
	 *
	 * The comments are the one thing only a template gets: they tell a user how to fill a column in, which is meaningless on an export they are only reading.
	 *
	 * Declared on the class rather than on `IFileTransfer` because a template is an Excel-shaped concern — header comments, column widths, an example row — with no JSON analogue, and `JsonFileTransfer` implements that same interface.
	 */
	public generateTemplate(fileName: string, schema: IFileDataSchema<T>): void {
		const properties = Object.entries<IFileDataPropertySchema<any>>(schema.properties);

		// A template IS an export of exactly one synthetic entity, so it goes through the same sheet
		// builder rather than reimplementing the row loop — that is what guarantees the example row
		// obeys the same type coercion and cell-length limit a real export does.
		const exampleEntity = Object.fromEntries(properties.map(([key, propSchema]) => [key, propSchema.example])) as T;

		const ws = this.buildSheet([exampleEntity], schema);

		properties.forEach(([, propSchema], columnIndex) => {
			if (!propSchema.comment) {
				return;
			}

			// Schemas store i18n KEYS; resolve here. If the key has no translation, ngx-translate
			// echoes the key back — write no comment at all rather than leaking it into the file.
			const comment = this.translator.instant(propSchema.comment);
			if (!comment || comment === propSchema.comment) {
				return;
			}

			const cell = ws[XLSX.utils.encode_cell({ r: 0, c: columnIndex })];
			if (!cell) {
				return;
			}

			cell.c = cell.c || [];
			cell.c.push({ a: 'System', t: comment });
		});

		this.writeSheet(fileName, schema, ws);
	}

	buildColumnValue(key: string, value: any, propType: FileDataPropertyType<any>): any {
		if (typeof propType === 'string') {
			// eslint-disable-next-line valid-typeof
			if (typeof value !== propType) {
				throw new TypeError(
					this.translator.instant('transferService.excel.errors.invalid-field-type', {
						schemaKey: key,
						expectedType: propType,
						actualType: typeof value,
					}),
				);
			}

			return value;
		}

		if (typeof propType === 'object') {
			if ('array' in propType) {
				if (typeof propType.array !== 'string') {
					throw new TypeError('Only array of Primitives is supported in Excel import/export');
				}

				// if the value is an array the data type must be an array
				if (!Array.isArray(value)) {
					throw new TypeError(
						this.translator.instant('transferService.excel.errors.invalid-array-type', {
							schemaKey: key,
							expectedType: propType,
						}),
					);
				}
				return value.join(', ');
			} else if ('record' in propType) {
				if (typeof value !== 'object') {
					throw new TypeError(this.translator.instant('transferService.excel.errors.invalid-record-type', { schemaKey: key, expectedType: propType }));
				}

				// Convert record to string format "key1=value1, key2=value2"
				return Object.entries(value as Record<string, any>)
					.map(([k, v]) => `${k}=${v}`)
					.join(', ');
			}
		}
	}

	private buildFileMap<TRow>(schema: IFileDataSchema<TRow>): Record<string, IFileMapPropertySchema<any>> {
		return Object.entries(schema.properties).reduce((acc, propEntry) => {
			const [key, propertySchema] = propEntry as [string, IFileMapPropertySchema<any>];
			// Save the key since it may be overridden by fileKey
			propertySchema.key = key;

			acc[propertySchema.fileKey || key] = propertySchema; // Use fileKey if available, otherwise use key
			return acc;
		}, {});
	}

	/** The buffer -> workbook step, split out of {@link importData} so {@link importWorkbook} can parse a file ONCE and read several of its sheets, instead of re-parsing the same buffer once per sheet. */
	private parseWorkbook(file: ArrayBuffer): XLSX.WorkBook {
		if (file.byteLength === 0) {
			throw new Error(this.translator.instant('transferService.excel.errors.empty-file'));
		}

		try {
			return XLSX.read(file, { type: 'binary' });
		} catch {
			throw new Error(this.translator.instant('transferService.excel.errors.failed-file-read'));
		}
	}

	importData(file: ArrayBuffer, schema: IFileDataSchema<T>, errorList: xlsxImportError[]): Array<IImportedData<T>> {
		return this.importSheet(this.parseWorkbook(file), schema, errorList);
	}

	/**
	 * Several differently-shaped sheets (e.g. a config's single settings row alongside its list-shaped sub-tables) read from ONE uploaded workbook/file in one pass, instead of the caller re-parsing the same buffer once per sheet via repeated {@link importData} calls. Each schema's rows and parse errors come back keyed
	 * by that same schema object, so a caller handles every sheet's result without separately tracking which errors belong to which sheet.
	 *
	 * A schema that names a sheet the file doesn't contain, or whose row count/header is invalid, surfaces as that entry's own `errors` rather than aborting the whole read - one bad sheet shouldn't hide every other sheet's valid rows from the caller.
	 */
	importWorkbook(file: ArrayBuffer, schemas: Array<IFileDataSchema<any>>): Array<{ schema: IFileDataSchema<any>; rows: Array<IImportedData<any>>; errors: xlsxImportError[] }> {
		const workbook = this.parseWorkbook(file);

		return schemas.map((schema) => {
			const errors: xlsxImportError[] = [];
			try {
				const rows = this.importSheet(workbook, schema, errors);
				return { schema, rows, errors };
			} catch (error) {
				return { schema, rows: [], errors: [{ error: error.message, row: 0, value: '' }] };
			}
		});
	}

	private importSheet<TRow>(workbook: XLSX.WorkBook, schema: IFileDataSchema<TRow>, errorList: xlsxImportError[]): Array<IImportedData<TRow>> {
		const sheetName = schema.name;

		if (!Object.prototype.hasOwnProperty.call(workbook?.Sheets, sheetName)) {
			throw new Error(
				this.translator.instant('transferService.excel.errors.missing-sheet', {
					name: sheetName,
				}),
			);
		}

		const sheet = workbook.Sheets[sheetName];
		const firstRowIndex = sheet['!ref'] ? XLSX.utils.decode_range(sheet['!ref']).s.r : 0;
		const rows = XLSX.utils.sheet_to_json<Array<any>>(sheet, { header: 1 });

		const minRows = schema.allowEmpty ? 1 : 2;
		if (rows.length < minRows) {
			throw new Error(this.translator.instant('transferService.excel.errors.missing-data'));
		}

		const fileMap = this.buildFileMap(schema);
		const headerKeys = Object.keys(fileMap);

		// remove header
		const header = rows.shift();

		if (!header || header.every((cell) => cell === undefined || cell === null || cell === '')) {
			throw new Error(this.translator.instant('transferService.excel.errors.missing-data'));
		}

		// A required property implies a required column — one flag governs both.
		const requiredKeys = headerKeys.filter((key) => fileMap[key].required !== false);

		// The file's own column order, which is all the row loop below needs: it pairs `row[i]` with
		// `actualColumnOrder[i]`, both indexed off the header. Columns are matched to the schema by
		// NAME, so the order they appear in is irrelevant — a hand-edited file may reorder them, and
		// an optional column may sit anywhere or be absent. Requiring schema order additionally
		// rejected files that carried every required column, just not in the declared sequence.
		const actualColumnOrder: string[] = header.map((cell) => `${cell}`);

		// An unrecognised column would silently drop its values, and a repeated one would let the
		// last occurrence quietly win, so both are rejected rather than tolerated.
		const unexpected = actualColumnOrder.filter((cell) => !Object.prototype.hasOwnProperty.call(fileMap, cell));
		const duplicated = actualColumnOrder.filter((cell, index) => actualColumnOrder.indexOf(cell) !== index);
		if (unexpected.length > 0 || duplicated.length > 0) {
			throw new Error(
				this.translator.instant('transferService.excel.errors.invalid-header', {
					expectedHeaders: headerKeys.join(', '),
				}),
			);
		}

		const missingRequired = requiredKeys.filter((key) => !actualColumnOrder.includes(key));
		if (missingRequired.length > 0) {
			throw new Error(
				this.translator.instant('transferService.excel.errors.invalid-header', {
					expectedHeaders: requiredKeys.join(', '),
				}),
			);
		}

		const entities = rows.reduce((acc, row, rowIndex) => {
			const missingProperties = [];
			const entity = {} as TRow;

			// Skip empty rows
			if (row.every((cell) => cell === undefined || cell === null || cell === '')) {
				return acc;
			}

			// sheet_to_json starts at the used range; add the header and use Excel's 1-based rows.
			const realRowIndex = firstRowIndex + rowIndex + 2;

			for (let columnIndex = 0; columnIndex < actualColumnOrder.length; columnIndex++) {
				const element = row[columnIndex];
				const schemaKey = actualColumnOrder[columnIndex];
				const propertySchema = fileMap[schemaKey];
				// Check for required properties
				if (element === undefined || element === null || element === '') {
					if (propertySchema.required) missingProperties.push(schemaKey); // Collect missing required properties
					else continue; // Skip optional properties if value is empty
				} else {
					// Reject an over-cap cell rather than storing what is necessarily a truncation
					if (typeof element === 'string' && element.length > EXCEL_MAX_CELL_LENGTH) {
						errorList.push({
							error: this.translator.instant('transferService.excel.errors.cell-too-long-row', {
								rowIndex: realRowIndex,
								entity: schema.name,
								schemaKey,
								length: element.length,
								maxLength: EXCEL_MAX_CELL_LENGTH,
							}),
							row: realRowIndex,
							// Preview only — echoing a >32k cell back would balloon the error payload
							value: `${element.slice(0, OVER_CAP_PREVIEW_LENGTH)}…`,
						});
						return acc;
					}

					try {
						entity[propertySchema.key] = this.convertPropertyToType(element, propertySchema.type);
					} catch {
						errorList.push({
							error: this.translator.instant('transferService.excel.errors.invalid-row-type', {
								rowIndex: realRowIndex,
								schemaKey,
								expectedType: propertySchema.type,
								actualType: typeof element,
							}),
							row: realRowIndex, // +2 because we removed the header and rows are 1-indexed
							value: row.join(', '),
						});
						return acc; // Skip this row if there's an error
					}
				}
			}
			// skip empty rows
			if (missingProperties.length > 0) {
				errorList.push({
					error: this.translator.instant('transferService.excel.errors.missing-properties', { rowIndex: realRowIndex, missingProperties: missingProperties.join(', ') }),
					row: realRowIndex,
					value: row.join(', '),
				});
				return acc;
			}

			acc.push({
				data: entity,
				location: this.translator.instant('transferService.excel.row-location', {
					rowIndex: realRowIndex,
				}),
			});

			return acc;
		}, []);

		return entities;
	}

	convertPropertyToType(value: any, type: FileDataPropertyType<any>): any {
		if (typeof type === 'object') {
			if ('array' in type) {
				const arrayType = type.array;

				if (typeof arrayType !== 'string') {
					throw new TypeError('Only array of Primitives is supported in Excel import/export');
				}

				if (typeof value === 'string') value = value.split(',').map((v) => v.trim());
				// Handle array type
				return Array.isArray(value) ? value.map((item) => this.convertPropertyToType(item, arrayType)) : [];
			} else if ('record' in type) {
				const recordType = type.record;
				if (typeof value === 'string') {
					// Assume format is "key1:value1, key2:value2"
					const regex = /([^=,]+)=([^,]+)/g;

					const obj: Record<string, any> = {};
					for (const match of value.matchAll(regex)) {
						const key = match[1].trim();
						const val = match[2].trim();
						obj[key] = this.convertPropertyToType(val, recordType);
					}

					return obj;
				} else {
					throw new TypeError('Invalid record format');
				}
			}

			throw new Error('Invalid type definition');
		}

		switch (type) {
			case 'string':
				// eslint-disable-next-line ts/switch-exhaustiveness-check
				switch (typeof value) {
					case 'string':
						return value;
					case 'number':
						return value.toString();
					default:
						throw new Error(this.translator.instant('transferService.excel.errors.missing-properties', { type: typeof value }));
				}
			case 'number':
				// eslint-disable-next-line ts/switch-exhaustiveness-check
				switch (typeof value) {
					case 'string': {
						const num = Number.parseFloat(value);
						if (Number.isNaN(num)) {
							throw new TypeError(this.translator.instant('transferService.excel.errors.invalid-number', { value }));
						}
						return num;
					}
					case 'number':
						return value;
					default:
						throw new Error(this.translator.instant('transferService.excel.errors.missing-properties', { type: typeof value }));
				}
			case 'boolean':
				// eslint-disable-next-line ts/switch-exhaustiveness-check
				switch (typeof value) {
					case 'string':
						return ['true', '1'].includes(value.toLowerCase());
					case 'boolean':
						return value;
					default:
						throw new Error(this.translator.instant('transferService.excel.errors.missing-properties', { type: typeof value }));
				}
			default:
				throw new Error(
					this.translator.instant('transferService.excel.errors.missing-properties', {
						type: typeof value,
					}),
				);
		}
	}
}
